import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ticketAPI } from '../../services/api';
import { LogOut, MessageSquare, Clock, Bell } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import io from 'socket.io-client';
import './AdminDashboard.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const socketRef = useRef(null);
  const isMountedRef = useRef(true);
  const { notification, showNotification, cleanup } = useNotification();

  useEffect(() => {
    loadTickets();
    
    // Initialize WebSocket connection
    const socket = io(SOCKET_URL);
    socketRef.current = socket;
    
    // Join as admin
    socket.emit('join_system', {
      userId: user.id,
      username: user.username,
      isAdmin: true
    });
    
    // Listen for new tickets
    socket.on('new_ticket', (data) => {
      showNotification(`Yeni talep #${data.ticketId}: ${data.title}`);
      // Refresh tickets
      ticketAPI.getTickets().then(response => {
        if (response.success && isMountedRef.current) {
          setTickets(response.tickets || []);
        }
      }).catch(() => {});
    });
    
    // Listen for new messages from users
    socket.on('new_user_message', (data) => {
      showNotification(`${data.username} kullanıcısından talep #${data.ticketId} için yeni mesaj`);
      
      // Update ticket message count in local state immediately for real-time UI update
      if (isMountedRef.current) {
        setTickets(prevTickets => 
          prevTickets.map(ticket => 
            Number(ticket.id) === Number(data.ticketId)
              ? { ...ticket, message_count: (ticket.message_count || 0) + 1 }
              : ticket
          )
        );
      }
      
      // Also refresh tickets from server as backup
      ticketAPI.getTickets().then(response => {
        if (response.success && isMountedRef.current) {
          setTickets(response.tickets || []);
        }
      }).catch(() => {});
    });
    
    // Listen for ALL status updates (from any source)
    socket.on('ticket_status_changed', (data) => {
      // ALWAYS update ticket status in local state for real-time UI update
      if (isMountedRef.current) {
        setTickets(prevTickets => 
          prevTickets.map(ticket => 
            Number(ticket.id) === Number(data.ticketId)
              ? { ...ticket, status: data.newStatus }
              : ticket
          )
        );
      }
      
      // Show notification only if updated by another admin (not yourself)
      if (data.updatedBy && data.updatedBy !== user.username) {
        const statusText = getStatusText(data.newStatus);
        showNotification(`Talep #${data.ticketId} durumu ${data.updatedBy} tarafından güncellendi: ${statusText}`);
      }
    });
    
    return () => {
      isMountedRef.current = false;
      cleanup();
      socket.off('new_ticket');
      socket.off('new_user_message');
      socket.off('ticket_status_changed');
      socket.disconnect();
    };
  }, []);
  

  const loadTickets = async () => {
    try {
      const response = await ticketAPI.getTickets();
      
      if (response.success) {
        setTickets(response.tickets || []);
      }
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleTicketClick = (ticketId) => {
    navigate(`/ticket/${ticketId}`);
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Beklemede',
      'open': 'Açık',
      'in_progress': 'İşlemde',
      'closed': 'Kapalı'
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'pending': 'status-pending',
      'open': 'status-open',
      'in_progress': 'status-progress',
      'closed': 'status-closed'
    };
    return statusMap[status] || 'status-pending';
  };

  const handleStatusChange = async (ticketId, newStatus, currentStatus) => {
    if (newStatus === currentStatus) return;
    
    setUpdatingStatus(prev => ({ ...prev, [ticketId]: true }));
    
    // Update UI immediately for better user experience (optimistic update)
    const originalTickets = [...tickets];
    if (isMountedRef.current) {
      setTickets(prevTickets => 
        prevTickets.map(ticket => 
          Number(ticket.id) === Number(ticketId)
            ? { ...ticket, status: newStatus }
            : ticket
        )
      );
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/update_status.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ticket_id: ticketId,
          status: newStatus
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const statusText = getStatusText(newStatus);
        let message = `Talep #${ticketId} durumu güncellendi: ${statusText}`;
        if (data.sms_sent) {
          message += ' (SMS gönderildi ✓)';
        }
        if (data.notification_sent) {
          message += ' (Kullanıcı bilgilendirildi ✓)';
        }
        showNotification(message);
        
        // Also emit via WebSocket directly as backup
        if (socketRef.current) {
          socketRef.current.emit('status_updated', {
            ticketId: Number(ticketId),
            oldStatus: currentStatus,
            newStatus: newStatus,
            updatedBy: user.username
          });
        }
      } else {
        // Revert optimistic update on failure
        if (isMountedRef.current) {
          setTickets(originalTickets);
        }
        showNotification(`Durum güncellenemedi: ${data.error}`);
      }
    } catch (error) {
      // Revert optimistic update on error
      if (isMountedRef.current) {
        setTickets(originalTickets);
      }
      showNotification('Talep durumu güncellenirken hata oluştu');
    } finally {
      // Always clear the updating status, regardless of success or failure
      setUpdatingStatus(prev => {
        const newState = { ...prev };
        delete newState[ticketId];
        return newState;
      });
    }
  };

  const getCategoryClass = (category) => {
    return `badge-${category.toLowerCase()}`;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'şimdi';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} dakika önce`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} saat önce`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} gün önce`;
    return date.toLocaleDateString('tr-TR');
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'all') return true;
    return ticket.status === filter;
  });

  return (
    <div className="admin-dashboard-container">
      {/* Notification Banner */}
      {notification && (
        <div className="notification-banner">
          <Bell size={20} />
          <span>{notification}</span>
        </div>
      )}
      
      {/* Header */}
      <header className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Yönetim Paneli</h1>
            <p>Tüm talepleri ve destek isteklerini yönetin</p>
          </div>
          <div className="header-right">
            <button className="btn-secondary" onClick={handleLogout}>
              <LogOut size={18} />
              Çıkış
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-value">{tickets.filter(t => t.status === 'pending').length}</div>
          <div className="stat-label">Beklemede</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{tickets.filter(t => t.status === 'open').length}</div>
          <div className="stat-label">Açık</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{tickets.filter(t => t.status === 'closed').length}</div>
          <div className="stat-label">Kapalı</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{tickets.length}</div>
          <div className="stat-label">Toplam Talep</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Tümü
        </button>
        <button 
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Beklemede
        </button>
        <button 
          className={`filter-btn ${filter === 'open' ? 'active' : ''}`}
          onClick={() => setFilter('open')}
        >
          Açık
        </button>
        <button 
          className={`filter-btn ${filter === 'closed' ? 'active' : ''}`}
          onClick={() => setFilter('closed')}
        >
          Kapalı
        </button>
      </div>

      {/* Tickets List */}
      <div className="admin-tickets-container">
        {loading ? (
          <div className="loading">Talepler yükleniyor...</div>
        ) : filteredTickets.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={64} />
            <h3>Talep bulunamadı</h3>
            <p>Mevcut filtreye uygun talep yok</p>
          </div>
        ) : (
          filteredTickets.map((ticket) => (
          <div 
  key={ticket.id} 
  className="admin-ticket-card"
>
  {/* ✅ Ticket ID Badge */}
  <div className="ticket-id-badge"># {ticket.id}</div>

  <div className="ticket-header" onClick={() => handleTicketClick(ticket.id)}>
    <div className="ticket-title-section">
      <h3 className="ticket-title">{ticket.title}</h3>
      <span className="ticket-user">tarafından {ticket.username || 'Kullanıcı'}</span>
    </div>
    <div className="ticket-time">
      <Clock size={14} />
      {formatTimeAgo(ticket.last_message_at || ticket.created_at)}
    </div>
  </div>
  
  <div className="ticket-meta" onClick={() => handleTicketClick(ticket.id)}>
    <span className={`ticket-badge ${getCategoryClass(ticket.category)}`}>
      {ticket.category}
    </span>
    <span className={`ticket-status ${getStatusClass(ticket.status)}`}>
      {getStatusText(ticket.status)}
    </span>
    <span className="ticket-badge">
      <MessageSquare size={14} />
      {ticket.message_count || 0} mesaj
    </span>
    <div className="ticket-status-update" onClick={(e) => e.stopPropagation()}>
      <select 
        className="status-select"
        value={ticket.status}
        onChange={(e) => handleStatusChange(ticket.id, e.target.value, ticket.status)}
        disabled={updatingStatus[ticket.id]}
      >
        <option value="pending">Beklemede</option>
        <option value="open">Açık</option>
        <option value="closed">Kapalı</option>
      </select>
      {updatingStatus[ticket.id] && (
        <span className="updating-indicator">Güncelleniyor...</span>
      )}
    </div>
  </div>
</div>

          ))
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
